import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { Currency } from '../../models/serializable/currency.js';
import { EventTicketOrder } from '../../models/serializable/event-ticket-order.js';
import { Price } from '../../models/serializable/price.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { DiscountHelper } from '../discount-helper.js';
import { TicketPricing } from '../ticket-pricing.js';
import { IOrderPriceBreakdown } from './order-price-breakdown.js';
import { TicketOrderDiscountMode } from './ticket-order-discount-mode.js';
import { IPaymentHelperAdapter } from './payment-helper-adapter.js';
import {
  isTicketOrderIncomeConfig, isTicketOrderRefundConfig, IPaymentHelperConfig,
} from './payment-helper-config.js';
import { ArgumentException } from '../../exceptions.js';
import { LegacyOrderDiscountBreakdown, LegacyOrderDiscountBreakdownEntry } from './discount-breakdown.js';
import { LegacyOrderFeeBreakdown, LegacyOrderFeeBreakdownEntry } from './fee-breakdown.js';
import { FeeHelper } from '../fee-helper.js';

export class OfflineTicketOrderPaymentHelper extends IPaymentHelperAdapter {
  public readonly orderTypeId: string = EventTicketOrder.TYPE_ID;

  static verifyType(dm: IPaymentHelperAdapter): boolean {
    return dm instanceof OfflineTicketOrderPaymentHelper;
  }

  static assertType(dm: IPaymentHelperAdapter): dm is OfflineTicketOrderPaymentHelper {
    return dm instanceof OfflineTicketOrderPaymentHelper;
  }

  getCurrencyOfOrder(order: CustomerOrderBase): Currency | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return order.tickets.length > 0 ? order.tickets[0].template.price.currency : null;
  }

  calculateDiscountForOrder(order: CustomerOrderBase): Price | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculateDiscountForOfflineTicketOrder(order);
  }

  calculateCheckoutPriceBreakdownForOnlineOrder(order: CustomerOrderBase): IOrderPriceBreakdown | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateDiscountBreakdownForOrder(order: CustomerOrderBase): LegacyOrderDiscountBreakdown | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const pricing = TicketPricing.calculate(order);
    if(pricing == null) return null;
    const entries: LegacyOrderDiscountBreakdownEntry[] = [];

    for(let ti = 0; ti < pricing.tickets.length; ti++) {
      const ticketPricing = pricing.tickets[ti];
      if(DiscountHelper.hasDiscountValue(ticketPricing.ticket.discount)) {
        entries.push({ price: ticketPricing.ticketDiscount, host: { type: 'ticket', index: ti } });
      }
    }

    if(DiscountHelper.hasDiscountValue(order.discount)) {
      entries.push({ price: pricing.groupDiscountTotal, host: { type: 'order' } });
    }

    return { entries };
  }

  calculateFeeBreakdownForOrder(order: CustomerOrderBase): LegacyOrderFeeBreakdown | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const entries: LegacyOrderFeeBreakdownEntry[] = [];

    for(let ti = 0; ti < order.tickets.length; ti++) {
      const ticket = order.tickets[ti];
      const result = FeeHelper.calculateFeeValueForEventTicket(ticket);

      if(result != null) {
        entries.push({ ...result, host: { type: 'ticket', index: ti } });
      }
    }

    for(let pi = 0; pi < order.products.length; pi++) {
      const orderItem = order.products[pi];
      const result = FeeHelper.calculateFeeValueForProductOrderItem(orderItem, { applyScaling: false });

      if(result != null) {
        entries.push({ ...result, host: { type: 'product', index: pi } });
      }
    }

    return { entries };
  }

  calculateCheckoutPriceForOrder(order: CustomerOrderBase): Price | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOfflineTicketOrder({
      order,
      applyProducts: true,
      applySupplements: true,
      applyDonation: true,
      applyFee: true,
      discountMode: 'Everything',
    });
  }

  calculateCheckoutPriceForDiscountProvider(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    // by default discounts applied to the order only take into account the tickets, not the products
    let includeProducts: boolean = false;
    let includeSupplements: boolean = false;

    if(isTicketOrderIncomeConfig(config)) {
      includeProducts = config.applyProducts;
      includeSupplements = config.applySupplements;
    }

    return this.calculatePriceForOfflineTicketOrder({
      order,
      applyProducts: includeProducts,
      applySupplements: includeSupplements,
      applyDonation: false,
      applyFee: false,
      discountMode: 'TicketsOnly',
    });
  }

  calculateIncomeForOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isTicketOrderIncomeConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculatePriceForOfflineTicketOrder({
      order,
      applyProducts: config.applyProducts,
      applySupplements: config.applySupplements,
      applyDonation: false,
      applyFee: false,
      discountMode: 'Everything',
    });
  }

  calculateIncomeWithoutDiscountForOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isTicketOrderIncomeConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculatePriceForOfflineTicketOrder({
      order,
      applyProducts: config.applyProducts,
      applySupplements: config.applySupplements,
      applyDonation: false,
      applyFee: false,
      discountMode: 'None',
    });
  }

  canCreateRefundForOnlineOrder(order: CustomerOrderBase): boolean | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateExistingRefundForOrder(order: CustomerOrderBase): Price | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculateExistingRefundForOfflineTicketOrder(order);
  }

  calculateRemainingRefundForOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isTicketOrderRefundConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculateRemainingRefundForOfflineTicketOrder(order, config);
  }

  calculateMaxRefundForOnlineOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!EventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isTicketOrderRefundConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculateMaxRefundForOfflineTicketOrder(order, config);
  }

  private calculateDiscountForOfflineTicketOrder(order: EventTicketOrder): Price | null {
    if(isNullOrUndefined(order.discount)) {
      return null;
    }

    return TicketPricing.calculate(order)?.groupDiscountTotal ?? null;
  }

  private calculatePriceForOfflineTicketOrder({ order, applyProducts, applySupplements, applyFee, applyDonation, discountMode }: {
    order: EventTicketOrder,
    applyProducts: boolean,
    applySupplements: boolean,
    applyFee: boolean,
    applyDonation: boolean,
    discountMode: TicketOrderDiscountMode,
  }): Price | null {
    let currency: Currency | null = null;
    if(order.tickets.length > 0) {
      currency = order.tickets[0].template.price.currency;
    }

    if(isNullOrUndefined(currency)) {
      return null;
    }

    const ticketPricing = TicketPricing.calculate(order, discountMode);
    if(ticketPricing == null) return null;
    let orderPrice = ticketPricing.total;

    if(applyProducts) {
      for(const item of order.products) {
        if(item.product.price.currency !== currency) {
          return null;
        }

        orderPrice = orderPrice.add(item.product.price.scale(item.quantity));
      }
    }

    if(applySupplements) {
      for(const ticket of order.tickets) {
        if(ticket.supplement == null) {
          continue;
        }
        if(ticket.supplement.template.price.currency !== currency) {
          return null;
        }

        const supplementPrice = ticket.supplement.template.price;
        orderPrice = orderPrice.add(supplementPrice);
      }
    }

    if(applyFee) {
      if(order.standardApplicationFee.isSome) {
        orderPrice = orderPrice.add(order.standardApplicationFee.value);
      }
      if(isNotNullOrUndefined(order.productApplicationFee)) {
        orderPrice = orderPrice.add(order.productApplicationFee);
      }
    }

    if(applyDonation && isNotNullOrUndefined(order.donation)) {
      orderPrice = orderPrice.add(order.donation);
    }

    return orderPrice;
  }

  private calculateExistingRefundForOfflineTicketOrder(order: EventTicketOrder): Price | null {
    let currency: Currency | null = null;
    if(order.tickets.length > 0) {
      currency = order.tickets[0].template.price.currency;
    }

    if(isNullOrUndefined(currency)) {
      return null;
    }

    let totalRefundSoFar = Price.zero(currency);
    for(const refund of order.refunds) {
      totalRefundSoFar = totalRefundSoFar.add(refund.price);
    }

    return totalRefundSoFar;
  }

  private calculateRemainingRefundForOfflineTicketOrder(
      order: EventTicketOrder,
      options: {
        includeStandardFee: boolean,
        includeProductFee: boolean,
        includeProducts: boolean,
        includeSupplements: boolean,
      }
  ): Price | null {
    let orderPrice = this.calculateIncomeForOrder(
        order,
        { applyProducts: options.includeProducts, applySupplements: options.includeSupplements }
    );

    if(isNullOrUndefined(orderPrice)) {
      return null;
    }

    if(order.standardApplicationFee.isSome && options.includeStandardFee) {
      orderPrice = orderPrice.add(order.standardApplicationFee.value);
    }

    if(order.productApplicationFee != null && options.includeProductFee) {
      orderPrice = orderPrice.add(order.productApplicationFee);
    }

    let totalRefundSoFar = Price.zero(orderPrice.currency);
    for(const refund of order.refunds) {
      totalRefundSoFar = totalRefundSoFar.add(refund.price);
    }

    return orderPrice.difference(totalRefundSoFar);
  }

  private calculateMaxRefundForOfflineTicketOrder(
      order: EventTicketOrder,
      options: {
        includeStandardFee: boolean,
        includeProductFee: boolean,
        includeProducts: boolean,
        includeSupplements: boolean,
      }
  ): Price | null {
    let orderPrice = this.calculateIncomeForOrder(
        order,
        { applyProducts: options.includeProducts, applySupplements: options.includeSupplements }
    );

    if(isNullOrUndefined(orderPrice)) {
      return null;
    }

    if(order.standardApplicationFee.isSome && options.includeStandardFee) {
      orderPrice = orderPrice.add(order.standardApplicationFee.value);
    }

    if(order.productApplicationFee != null && options.includeProductFee) {
      orderPrice = orderPrice.add(order.productApplicationFee);
    }

    return orderPrice;
  }
}
